import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/client.js';
import type { Department } from '../../api/endpoints.js';
import { orgApi } from '../../api/endpoints.js';
import { queryKeys } from '../../app/query-client.js';
import { HeaderCell, TableHeaderRow, TableShell } from '../../components/data-table.js';
import { HelpHint } from '../../components/help-hint.js';
import { PageHeader } from '../../components/page-header.js';
import { Select } from '../../components/select.js';
import { Alert, Badge, Button, COMPACT_CONTROL as FIELD, Spinner } from '../../components/ui.js';
import { useDepartments } from '../../hooks/use-directory.js';

const ANY = 'any';

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

const DEPARTMENTS_HINT = (
  <>
    <p>
      A department is <strong className="font-semibold text-navy dark:text-dark-ink">where work
      lives</strong>. A request always sits in exactly one, and that is what decides who sees it
      and who may act on it.
    </p>
    <p>
      Separate from a position, which decides what somebody may{' '}
      <em>raise</em>. The two are set independently on each person, and nothing requires them to
      match.
    </p>
    <p>
      Everyone may file a request into every department — that never needs setting up. Working in
      one is what has to be granted, on the People screen.
    </p>
  </>
);

const PEOPLE_HINT = (
  <>
    <p>
      Active accounts that <strong className="font-semibold text-navy dark:text-dark-ink">work
      here</strong> — their home department, or one raised above the default on the People
      screen.
    </p>
    <p>
      It is not "who may send us a request", which is everybody. A department showing zero is one
      nobody can see the queue of.
    </p>
  </>
);

const OPEN_HINT = (
  <p>
    Requests currently sitting here that are not finished. A department cannot be closed while
    this is above zero — move or finish them first.
  </p>
);

const STATUS_HINT = (
  <>
    <p>
      Closing a department takes it out of every picker: nobody can file into it, and it stops
      being offered as a home department. Existing requests are untouched.
    </p>
    <p>
      Two things refuse it: open requests, and being{' '}
      <strong className="font-semibold text-navy dark:text-dark-ink">built in</strong>. Finance,
      Operations and Human Resources carry the approval routing of the Buy and Hire boards, so
      the system will not let them go.
    </p>
  </>
);

export function DepartmentsPage(): ReactNode {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('true');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const { departments: allDepartments, isLoading } = useDepartments(true);

  const fail = (fallback: string) => (error: unknown) => {
    setSaved(null);
    setProblem(error instanceof ApiError ? error.message : fallback);
  };

  const done = async (id: string | null): Promise<void> => {
    setProblem(null);
    setSaved(id);
    setEditing(null);
    await queryClient.invalidateQueries({ queryKey: queryKeys.org.departments.all });
    await queryClient.invalidateQueries({ queryKey: queryKeys.boards.all });
  };

  const update = useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Parameters<typeof orgApi.updateDepartment>[1]) =>
      orgApi.updateDepartment(id, input),
    onSuccess: (department) => done(department.id),
    onError: fail('That department could not be changed.'),
  });

  const create = useMutation({
    mutationFn: () => orgApi.createDepartment({ slug: slugify(newName), name: newName.trim() }),
    onSuccess: async (department) => {
      setNewName('');
      setAdding(false);
      await done(department.id);
    },
    onError: fail('That department could not be created.'),
  });

  const needle = search.trim().toLowerCase();

  const items = allDepartments.filter((entry) => {
    if (statusFilter !== ANY && entry.isActive !== (statusFilter === 'true')) return false;
    return needle === '' || entry.name.toLowerCase().includes(needle);
  });

  const newSlug = slugify(newName);
  const mayCreate = newName.trim().length >= 2 && newSlug.length >= 2;

  return (
    <>
      <PageHeader title="Departments" subtitle="Where requests live, and who works in each one.">
        <HelpHint title="Departments">{DEPARTMENTS_HINT}</HelpHint>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            setAdding(true);
            setProblem(null);
          }}
        >
          Add department
        </Button>
      </PageHeader>

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
        {problem === null ? null : <Alert tone="error">{problem}</Alert>}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            placeholder="Search departments"
            className="h-11 min-w-[220px] flex-1 rounded-field border border-[rgb(223,228,235)] bg-white px-3.5 text-sm font-normal text-[rgb(20,32,51)] outline-none focus:border-royal/55 dark:border-dark-line dark:bg-dark-field dark:text-dark-ink"
          />

          <Select
            label="Status filter"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'true', label: 'Open' },
              { value: 'false', label: 'Closed' },
              { value: ANY, label: 'All departments' },
            ]}
          />
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-20 text-muted">
            <Spinner className="size-5" />
          </div>
        ) : (
          <TableShell>
            <TableHeaderRow>
              <HeaderCell className="min-w-[120px] max-w-[28rem] flex-1">Department</HeaderCell>
              <HeaderCell className="hidden w-24 shrink-0 items-center gap-1.5 sm:flex">
                People
                <HelpHint title="People">{PEOPLE_HINT}</HelpHint>
              </HeaderCell>
              <HeaderCell className="hidden w-24 shrink-0 items-center gap-1.5 md:flex">
                Open
                <HelpHint title="Open requests">{OPEN_HINT}</HelpHint>
              </HeaderCell>
              <HeaderCell className="flex w-[170px] shrink-0 items-center justify-end gap-1.5">
                Status
                <HelpHint title="Status">{STATUS_HINT}</HelpHint>
              </HeaderCell>
            </TableHeaderRow>

            {adding ? (
              <div className="flex w-full flex-wrap items-center gap-3 border-b border-hairline bg-royal/4 px-4 py-3 dark:border-dark-line dark:bg-white/5">
                <input
                  autoFocus
                  value={newName}
                  placeholder="e.g. Customer Success"
                  onChange={(event) => {
                    setNewName(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && mayCreate) create.mutate();
                    if (event.key === 'Escape') setAdding(false);
                  }}
                  className={`${FIELD} min-w-[200px] flex-1`}
                />

                <span className="hidden text-[12.5px] text-muted sm:block">
                  {newSlug === '' ? 'Open to requests straight away' : `Identifier: ${newSlug}`}
                </span>

                <Button
                  type="button"
                  size="sm"
                  loading={create.isPending}
                  disabled={!mayCreate}
                  onClick={() => {
                    create.mutate();
                  }}
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setAdding(false);
                    setNewName('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : null}

            {items.map((entry, index) =>
              editing === entry.id ? (
                <EditRow
                  key={entry.id}
                  department={entry}
                  busy={update.isPending}
                  onCancel={() => {
                    setEditing(null);
                  }}
                  onSave={(input) => {
                    update.mutate({ id: entry.id, ...input });
                  }}
                />
              ) : (
                <DepartmentRow
                  key={entry.id}
                  department={entry}
                  first={index === 0 && !adding}
                  justSaved={saved === entry.id}
                  busy={update.isPending}
                  onEdit={() => {
                    setEditing(entry.id);
                    setProblem(null);
                  }}
                  onToggle={() => {
                    update.mutate({ id: entry.id, isActive: !entry.isActive });
                  }}
                />
              ),
            )}

            {items.length === 0 ? (
              <p className="px-4 py-10 text-center text-[13.5px] text-muted">
                Nothing matches that.
              </p>
            ) : null}
          </TableShell>
        )}
      </div>
    </>
  );
}

function DepartmentRow({
  department,
  first,
  justSaved,
  busy,
  onEdit,
  onToggle,
}: {
  readonly department: Department;
  readonly first: boolean;
  readonly justSaved: boolean;
  readonly busy: boolean;
  readonly onEdit: () => void;
  readonly onToggle: () => void;
}): ReactNode {
  const blocked = department.isActive
    ? department.isSystem
      ? 'Built in — it carries the approval routing of the Buy and Hire boards.'
      : department.openTicketCount > 0
        ? `Finish or move the ${String(department.openTicketCount)} open ${
            department.openTicketCount === 1 ? 'request' : 'requests'
          } first.`
        : null
    : null;

  return (
    <div
      className={`flex w-full items-center gap-4 px-4 py-2.5 ${
        first ? '' : 'border-t border-hairline dark:border-dark-line'
      } ${department.isActive ? '' : 'bg-mist/40 dark:bg-white/3'}`}
    >
      <span className="min-w-[120px] max-w-[28rem] flex-1">
        <span className="block truncate text-[14px] font-semibold text-navy dark:text-dark-ink">
          {department.name}
          {department.isSystem ? (
            <>
              {' '}
              <Badge tone="gold">Built in</Badge>
            </>
          ) : null}
          {department.isActive ? null : (
            <>
              {' '}
              <Badge tone="neutral">Closed</Badge>
            </>
          )}
          {justSaved ? (
            <>
              {' '}
              <Badge tone="positive">Saved</Badge>
            </>
          ) : null}
        </span>
        {department.description === null ? null : (
          <span className="block truncate text-[12px] text-muted">{department.description}</span>
        )}
        <span className="block truncate text-[12px] text-muted sm:hidden">
          {department.memberCount} working · {department.openTicketCount} open
        </span>
      </span>

      <span className="hidden w-24 shrink-0 text-[12.5px] text-muted sm:block">
        {department.memberCount === 0 ? '—' : department.memberCount}
      </span>

      <span className="hidden w-24 shrink-0 text-[12.5px] text-muted md:block">
        {department.openTicketCount === 0 ? '—' : department.openTicketCount}
      </span>

      <span className="flex w-[170px] shrink-0 justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={onEdit}>
          Edit
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy || blocked !== null}
          {...(blocked === null ? {} : { title: blocked })}
          onClick={onToggle}
        >
          {department.isActive ? 'Close' : 'Reopen'}
        </Button>
      </span>
    </div>
  );
}

function EditRow({
  department,
  busy,
  onCancel,
  onSave,
}: {
  readonly department: Department;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSave: (input: { name: string; description: string | null }) => void;
}): ReactNode {
  const [name, setName] = useState(department.name);
  const [description, setDescription] = useState(department.description ?? '');

  const ready = name.trim().length >= 2;

  const save = (): void => {
    if (!ready) return;
    onSave({ name: name.trim(), description: description.trim() === '' ? null : description.trim() });
  };

  return (
    <div className="flex w-full flex-wrap items-center gap-3 border-t border-hairline bg-royal/4 px-4 py-3 dark:border-dark-line dark:bg-white/5">
      <input
        autoFocus
        value={name}
        aria-label="Department name"
        onChange={(event) => {
          setName(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') save();
          if (event.key === 'Escape') onCancel();
        }}
        className={`${FIELD} min-w-[120px] flex-1`}
      />

      <input
        value={description}
        aria-label="Description"
        placeholder="Description (optional)"
        onChange={(event) => {
          setDescription(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') save();
          if (event.key === 'Escape') onCancel();
        }}
        className={`${FIELD} min-w-[200px] flex-[2]`}
      />

      <span className="hidden text-[12.5px] text-muted lg:block">{department.slug}</span>

      <Button type="button" size="sm" loading={busy} disabled={!ready} onClick={save}>
        Save
      </Button>
      <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
